import { COORDINATOR_ROLE, ORGANISATION_ADMIN_ROLE, STARTED } from '@careapp-group/core';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromAnnouncements from '../announcements/announcements.reducer';
import * as fromCustomerLists from '../customer-lists/customer-lists.reducer';
import * as fromAuth from '../auth';
import { auth } from '../auth/auth.selectors';
import * as fromChats from '../chats/chats.reducer';
import * as fromCheckInTags from '../check-in-tags/check-in-tags.reducer';
import * as fromCheckIns from '../check-ins/check-ins.reducer';
import * as fromCustomerTeam from '../customer-team/customer-team.reducer';
import * as fromCustomers from '../customers/customers.reducer';
import * as fromDataExport from '../data-export/data-export.reducer';
import {
  CHATS,
  CUSTOMERS,
  getAll,
  getEntities,
  getLoadingEntities,
  getSelectedId,
  MEMORIAMS,
  ORGANISATIONS,
  MEMBERS,
} from '../data-list.reducer';
import { DataListState } from '../data-list.state';
import * as fromConversations from '../conversations/conversations.reducer';
import * as fromCore from '../core/core.reducer';
import * as fromEvents from '../events/events.reducer';
import * as fromFeed from '../feed/feed.reducer';
import * as fromFeedback from '../feedback/feedback.reducer';
import * as fromFileUpload from '../file-upload/file-upload.reducer';
import * as fromFiles from '../files/files.reducer';
import * as fromEnquiries from '../../enquiries/store/enquiries.reducer';
import * as fromEnquiriesTenantCategories from '../../enquiries/store/tenant-categories/enquiries-tenant-categories.reducer';
import * as fromEnquiriesTenantSettings from '../../enquiries/store/tenant-settings/enquiries-tenant-settings.reducer';
import * as fromInvite from '../invite/invite.reducer';
import * as fromLocation from '../location/location.reducer';
import * as fromMarketManagementCategories from '../market-management-category/market-management-category.reducer';
import * as fromMemberCustomers from '../member-customers/member-customers.reducer';
import * as fromMemoCategories from '../memo-categories/memo-categories.reducer';
import * as fromMemoriams from '../memoriams/memoriams.reducer';
import * as fromMemos from '../memos/memos.reducer';
import * as fromNotifications from '../notifications/notifications.reducer';
import * as fromOrganisations from '../organisations/organisations.reducer';
import * as fromRemoteConfig from '../remote-config/remote-config.reducer';
import * as fromRequests from '../requests/requests.reducer';
import * as fromSettings from '../settings/settings.reducer';
import * as fromStaffCustomers from '../staff-customers/staff-customers.reducer';
import * as fromMembers from '../members/members.reducer';
import * as fromMemberBadges from '../member-badges/member-badges.reducer';
import * as fromStatistics from '../statistics/statistics.reducer';
import * as fromStorageSync from '../storage-sync/storage-sync.reducer';
import * as fromUi from '../ui/ui.reducer';
import * as fromBulk from './bulk-request.reducer';
import * as fromReleaseNotes from '../release-notes/release-notes.reducer';
import * as fromQuestions from '../questions/questions.reducer';
import * as fromTenants from '../tenants/tenants.reducer';
import { logoutReducer } from './logout.reducer';
import {
  ChatDto,
  CheckInDto,
  CheckInTagDto,
  CustomerDto,
  CustomerListDto,
  FeedbackDto,
  MarketCategoryDto,
  ModelEventDto,
  OrganisationDto,
  TenantDto,
} from '@careapp-group/ng-sdk';
import { MemberDto, PermissionRequestDto } from '@careapp-group/ng-sdk';
import { CustomersStateInterface } from '../customers/customers.state';
import { CaEntityState } from '../entity-state/entity-state';
import * as fromStream from '../stream/stream.reducer';

export * from '../data-list.reducer';
export interface State {
  announcements: fromAnnouncements.AnnouncementsState;
  auth: fromAuth.State;
  bulkAction: fromBulk.State;
  chats: DataListState<ChatDto>;
  checkIns: DataListState<CheckInDto>;
  checkInTags: DataListState<CheckInTagDto>;
  conversations: fromConversations.ConversationsStateInterface;
  core: fromCore.CoreState;
  customers: CustomersStateInterface;
  customerTeam: DataListState<MemberDto>;
  customerLists: DataListState<CustomerListDto>;
  dataExport: fromDataExport.State;
  events: CaEntityState<ModelEventDto>;
  enquiries: fromEnquiries.EnquiriesState;
  enquiriesTenantCategories: fromEnquiriesTenantCategories.EnquiriesTenantCategoryState;
  enquiriesTenantSettings: fromEnquiriesTenantSettings.EnquiriesTenantSettingsState;
  feed: DataListState<MemberDto>;
  feedback: DataListState<FeedbackDto>;
  fileUpload: fromFileUpload.State;
  files: fromFiles.FilesStateInterface;
  invite: fromInvite.State;
  location: fromLocation.State;
  marketManagementCategories: DataListState<MarketCategoryDto>;
  memoriams: fromMemoriams.MemoriamStateInterface;
  memos: fromMemos.MemosStateInterface;
  members: fromMembers.MembersStateInterface;
  memberBadges: fromMemberBadges.MemberBadgeState;
  memberCustomers: DataListState<CustomerDto>;
  memoCategories: fromMemoCategories.State;
  notifications: fromNotifications.NotificationsStateInterface;
  organisations: DataListState<OrganisationDto>;
  questions: fromQuestions.QuestionsStateInterface;
  requests: DataListState<PermissionRequestDto>;
  router: RouterReducerState;
  settings: fromSettings.State;
  staffCustomers: DataListState<CustomerDto>;
  storageSync: fromStorageSync.StorageSyncState;
  statistics: fromStatistics.State;
  stream: fromStream.StreamState;
  tenants: DataListState<TenantDto>;
  ui: fromUi.State;
  remoteConfig: fromRemoteConfig.RemoteConfigState;
  releaseNotes: fromReleaseNotes.ReleaseNotesState;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const REDUCERS = {
  auth: fromAuth.reducer,
  announcements: fromAnnouncements.reducer,
  bulkAction: fromBulk.reducer,
  chats: fromChats.reducer,
  checkIns: fromCheckIns.reducer,
  checkInTags: fromCheckInTags.reducer,
  conversations: fromConversations.conversationsReducer,
  core: fromCore.coreReducer,
  customers: fromCustomers.reducer,
  customerTeam: fromCustomerTeam.reducer,
  customerLists: fromCustomerLists.reducer,
  dataExport: fromDataExport.reducer,
  enquiries: fromEnquiries.reducer,
  enquiriesTenantCategories: fromEnquiriesTenantCategories.reducer,
  enquiriesTenantSettings: fromEnquiriesTenantSettings.reducer,
  events: fromEvents.reducer,
  feed: fromFeed.reducer,
  feedback: fromFeedback.reducer,
  fileUpload: fromFileUpload.fileUploadReducer,
  files: fromFiles.filesReducer,
  invite: fromInvite.reducer,
  location: fromLocation.reducer,
  marketManagementCategories: fromMarketManagementCategories.reducer,
  members: fromMembers.reducer,
  memberBadges: fromMemberBadges.reducer,
  memberCustomers: fromMemberCustomers.reducer,
  memoCategories: fromMemoCategories.reducer,
  memoriams: fromMemoriams.reducer,
  memos: fromMemos.reducer,
  notifications: fromNotifications.reducer,
  organisations: fromOrganisations.reducer,
  questions: fromQuestions.reducer,
  requests: fromRequests.reducer,
  router: routerReducer,
  settings: fromSettings.reducer,
  staffCustomers: fromStaffCustomers.reducer,
  statistics: fromStatistics.statsReducer,
  stream: fromStream.streamReducer,
  tenants: fromTenants.reducer,
  ui: fromUi.reducer,
  remoteConfig: fromRemoteConfig.reducer,
  releaseNotes: fromReleaseNotes.reducer,
};

export const META_REDUCERS = [fromStorageSync.reducer, logoutReducer];

/**
 * A selector function is a map function factory. We pass it parameters and it
 * returns a function that maps from the larger state tree into a smaller
 * piece of state. This selector simply selects the `books` state.
 *
 * Selectors are used with the `select` operator.
 *
 * ```ts
 * class MyComponent {
 * 	constructor(state$: Observable<State>) {
 * 	  this.booksState$ = state$.select(getBooksState);
 * 	}
 * }
 * ```
 */

export const getAnnouncementState = createFeatureSelector<State['announcements']>('announcements');
export const getBulkActionState = createFeatureSelector<State['bulkAction']>('bulkAction');
export const getChatsState = createFeatureSelector<State['chats']>('chats');
export const getCheckInsState = createFeatureSelector<State['checkIns']>('checkIns');
export const getCheckInTagsState = createFeatureSelector<State['checkInTags']>('checkInTags');
export const getConversationsState = createFeatureSelector<State['conversations']>('conversations');
export const getCustomersState = createFeatureSelector<State['customers']>('customers');
export const getCustomerTeamState = createFeatureSelector<State['customerTeam']>('customerTeam');
export const getCustomerListsState = createFeatureSelector<State['customerLists']>('customerLists');
export const getEnquiriesState = createFeatureSelector<State['enquiries']>('enquiries');
export const getFileUploadState = createFeatureSelector<State['fileUpload']>('fileUpload');
export const getFilesState = createFeatureSelector<State['files']>('files');
export const getInviteState = createFeatureSelector<State['invite']>('invite');
export const getLocationState = createFeatureSelector<State['location']>('location');
export const getMarketManagementCategoriesState = createFeatureSelector<
  State['marketManagementCategories']
>('marketManagementCategories');
export const getMemoCategoriesState =
  createFeatureSelector<State['memoCategories']>('memoCategories');
export const getMemoriamState = createFeatureSelector<State['memoriams']>('memoriams');
export const getOrganisationState =
  createFeatureSelector<fromOrganisations.OrganisationState>('organisations');
export const getQuestionsState = createFeatureSelector<State['questions']>('questions');
export const getRemoteConfigState = createFeatureSelector<State['remoteConfig']>('remoteConfig');
export const getRequestState = createFeatureSelector<State['requests']>('requests');
export const getReleaseNotesState = createFeatureSelector<State['releaseNotes']>('releaseNotes');
export const getRouterState = createFeatureSelector<State['router']>('router');
export const getSettingsState = createFeatureSelector<State['settings']>('settings');
export const getMembersState = createFeatureSelector<State['members']>('members');
export const getMemberBadgesState = createFeatureSelector<State['memberBadges']>('memberBadges');
export const getStaffCustomerState =
  createFeatureSelector<State['staffCustomers']>('staffCustomers');
export const getStatisticsState = createFeatureSelector<State['statistics']>('statistics');
export const getStorageSyncState = createFeatureSelector<State['storageSync']>('storageSync');
export const getStreamState = createFeatureSelector<State['stream']>('stream');
export const getTenantsState = createFeatureSelector<State['tenants']>('tenants');

/**
 * Every reducer module has selector functions but have no knowledge of overall state tree.
 * To make them useable we need to inject in their state segment so createSelector can be called
 */

export const bulk = fromBulk.createSelectors(getBulkActionState);
export const chats = fromChats.createSelectors(getChatsState);
export const checkInTags = fromCheckInTags.createSelectors(getCheckInTagsState);
export const customerLists = fromCustomerLists.createSelectors(getTenantsState);
export const invite = fromInvite.createSelectors(getInviteState);
export const memoCategories = fromMemoCategories.createSelectors(getMemoCategoriesState);
export const org = fromOrganisations.createSelectors(getOrganisationState);
export const settings = fromSettings.createSelectors(getSettingsState);
export const statistics = fromStatistics.createSelectors(getStatisticsState);
export const tenants = fromTenants.createSelectors(getTenantsState);

export const isCoordinator = createSelector(
  auth.isAdmin,
  org.currentOrgRoles,
  (rootAdmin, roles) => {
    return rootAdmin || roles.includes(COORDINATOR_ROLE);
  },
);
export const isOrgAdmin = createSelector(auth.isAdmin, org.currentOrgRoles, (rootAdmin, roles) => {
  return rootAdmin || roles.includes(ORGANISATION_ADMIN_ROLE);
});
export const isTenantAdmin = createSelector(
  auth.isAdmin,
  tenants.isTenantAdmin,
  (rootAdmin, tenantAdmin) => {
    return rootAdmin || tenantAdmin;
  },
);

export const getLocation = createSelector(getLocationState, fromLocation.getLocation);
export const isLocationLoading = createSelector(getLocationState, fromLocation.isLoading);

export const getRehydrated = createSelector(getStorageSyncState, fromStorageSync.getRehydrated);

export const getActiveCustomerChats = createSelector(
  getAll(CHATS),
  getEntities(CUSTOMERS),
  getEntities(MEMBERS),
  (chats: ChatDto[], customers, members) =>
    chats
      .filter((chat) => chat.status === STARTED)
      .map((chat) => ({
        chat,
        owner: members[chat.owner],
        customer: customers[chat.modelId],
      })),
);

export const getCurrentOrganisationCategories = createSelector(
  getSelectedId(ORGANISATIONS),
  memoCategories.getOrganisationCategories,
  (selectedId: string, orgCategories) =>
    orgCategories[selectedId]
      ? [...orgCategories[selectedId]].sort((a, b) => a.ordinal - b.ordinal)
      : [],
);

export const isMemoriamLoading = createSelector(
  getLoadingEntities(MEMORIAMS),
  getSelectedId(CUSTOMERS),
  getSelectedId(MEMORIAMS),
  (loadingEntities, customerId: string, memoriamId: string) =>
    !!loadingEntities[customerId] || !!loadingEntities[memoriamId],
);

export const isAppAuthorized = auth.isReady;

export const isAppReady = createSelector(getStorageSyncState, fromStorageSync.isReady);

/**
 * The selector that Storage Sync uses to decide what to write and when
 */
export const storageSyncSelector = createSelector(
  auth.getPersisted,
  org.getPersisted,
  (auth, organisations) => {
    return { auth, organisations };
  },
);
